import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

export type PremiumFeature = 'practice' | 'bundle' | 'guide' | 'offline' | 'challenge' | 'affirmations';

interface Props {
  visible: boolean;
  feature: PremiumFeature;
  onDismiss?: () => void;
}

const COPY: Record<PremiumFeature, { icon: string; title: string; desc: string }> = {
  practice: {
    icon: '🧠',
    title: 'Practice Mode is Premium',
    desc: 'Swipeable practice sessions help your child actively learn — not just read. Upgrade to unlock Practice Mode for every bundle.',
  },
  bundle: {
    icon: '📦',
    title: 'This bundle is Premium',
    desc: 'Deep-dive bundles go further on the toughest topics. Upgrade for full access to every bundle.',
  },
  guide: {
    icon: '📥',
    title: 'Parent Guides are Premium',
    desc: 'Downloadable conversation guides give you the exact words to use with your child. Upgrade to access them all.',
  },
  offline: {
    icon: '📴',
    title: 'Offline Mode is Premium',
    desc: 'Save scenarios to read anywhere — even without Wi-Fi. Upgrade to unlock offline access.',
  },
  challenge: {
    icon: '🏆',
    title: 'Family Challenges are Premium',
    desc: 'Weekly challenges build habits and reward progress with badges. Upgrade to take part every week.',
  },
  affirmations: {
    icon: '✨',
    title: 'Affirmations are Premium',
    desc: 'Daily affirmations with parent prompts help build confidence, resilience, and safety awareness. Upgrade to unlock all 80+ affirmations across every age group.',
  },
};

export default function PremiumGate({ visible, feature, onDismiss }: Props) {
  const navigate = useNavigate();
  const { icon, title, desc } = COPY[feature];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={onDismiss}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="bg-white rounded-3xl p-6 max-w-md w-full text-center shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="absolute top-4 right-4 text-gray-300 hover:text-gray-500"
              >
                <X size={20} />
              </button>
            )}

            <div className="text-5xl mb-3">{icon}</div>
            <span className="inline-block bg-warm-100 text-warm-700 text-xs font-bold px-3 py-1 rounded-full mb-3">
              ✨ Premium Feature
            </span>
            <h2 className="font-display text-xl font-extrabold text-primary-800 mb-2">
              {title}
            </h2>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">{desc}</p>

            <button
              onClick={() => navigate('/pricing')}
              className="w-full bg-warm-400 text-white py-3.5 rounded-2xl font-bold text-base shadow-md hover:bg-warm-500 transition-colors mb-3"
            >
              Upgrade to Premium →
            </button>

            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-gray-400 text-sm hover:text-gray-600 transition-colors"
              >
                Not now
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
